/**
 * Add above due to attempt to merge in a merge conflict fix, which is file is out of scope for
 * Did not make a ticket to fix the nested selector on this file, due to
 * stating RW team have tickets for the whole codebase
 */
import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const blogLinkStyles = css({
    margin: `${token("space.200")} 0 ${token("space.400")}`,
    fontSize: `${token("space.200")}`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        marginBlockEnd: `${token("space.100")}`
    }
});

const blogSectionStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: `${token("space.600")} 0 36px`,
    backgroundColor: "#ffffff"
});

const flexWrap = css({
    display: "flex",
    gap: "32px",
    [media.below.sm]: {
        flexWrap: "wrap",
        gap: 0
    }
});

const blogLinkTextStyles = css({
    "&:link, &:visited": {
        color: "#0052cc",
        textDecoration: "none"
    },

    "&:hover": {
        "color": "#0065ff",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".link-arrow": {
            transition: "margin-left 0.25s ease-in-out",
            marginInlineStart: token("space.050")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".link-text": {
            textDecoration: "underline"
        }
    }
});

export const NumberOneImage = () => {
    return (
        <Image
            src="https://dam-cdn.atl.orangelogic.com/AssetLink/42xb8t784240dh2sfq3yb5k8fuq22mf1.svg"
            alt="#1"
        />
    );
};

const BlogsSection = () => {
    const intl = useIntl();

    return (
        <section css={blogSectionStyles}>
            <Row>
                <div
                    css={css({
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.above.md]: {
                            gridTemplateColumns:
                                "minmax(0, 1fr) minmax(0, 1fr)",
                            gap: "32px"
                        }
                    })}
                >
                    <div
                        css={css({
                            [media.only.sm]: {
                                display: "grid",
                                gap: "32px",
                                gridTemplateColumns:
                                    "minmax(0, 1fr) minmax(0, 1fr)"
                            }
                        })}
                    >
                        <div
                            css={css({
                                width: "58.3%",
                                padding: `${token("space.200")} 0`,
                                [media.below.md]: {
                                    width: "90%",
                                    paddingInlineEnd: "0"
                                },

                                [media.above.md]: {
                                    transform: "translate3d(-30%, 0, 0)"
                                }
                            })}
                        >
                            <NumberOneImage />
                        </div>
                        <div
                            css={css({
                                marginInlineEnd: "15%",
                                padding: `${token("space.200")} 0`,
                                [media.below.md]: {
                                    marginInlineEnd: 0
                                },
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                h2: {
                                    fontSize: "2.3rem",
                                    color: "#344563",
                                    [media.below.sm]: {
                                        fontSize: "1.5rem"
                                    }
                                },
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                p: {
                                    fontSize: "1rem",
                                    fontWeight: "normal",
                                    color: "#344563"
                                }
                            })}
                        >
                            <ContentBlock
                                headerType="h2"
                                headerText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.blogsSection.headerText",
                                    defaultMessage:
                                        "Why Jira is the #1 tool recommended by agile teams.*"
                                })}
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.blogsSection.bodyText"
                                        defaultMessage="Trusted by more than 100,000 organizations, Jira comes out-of-the-box with the features and best practices agile teams need to develop and evolve their agile practices."
                                        values={DEFAULT_VALUES}
                                    />
                                }
                            />
                            <div
                                css={css({
                                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                    p: {
                                        fontSize: "0.9rem",
                                        color: "#42526e",
                                        margin: 0
                                    }
                                })}
                            >
                                <p>
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.blogsSection.contentFooter"
                                        defaultMessage="*2021 State of Agile Report"
                                        values={DEFAULT_VALUES}
                                    />
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div css={flexWrap}>
                            <AgileLink />
                            <TeamPlaybookLink />
                        </div>
                        <div css={flexWrap}>
                            <CommunityLink />
                            <DevOpsLink />
                        </div>
                    </div>
                </div>
            </Row>
        </section>
    );
};

export const AgileLink = () => {
    const intl = useIntl();

    return (
        <BlogLink
            imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/0r168a11o11n866jv042iaqtc2560d15.webp"
            imageAlt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.agileAltText",
                defaultMessage: "Agile illustration"
            })}
            href={useLocalizedHref("/agile")}
            linkText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.agileLinkText",
                defaultMessage: "Learn agile best practices"
            })}
        />
    );
};

export const TeamPlaybookLink = () => {
    const intl = useIntl();
    return (
        <BlogLink
            imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/e3buc7gjy550p1825lu05t674gf7116g.webp"
            imageAlt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.teamPlaybookAltText",
                defaultMessage: "Team playbook illustration"
            })}
            href={useLocalizedHref("/team-playbook")}
            linkText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.teamPlaybookLinkText",
                defaultMessage: "Team Playbook"
            })}
        />
    );
};

export const CommunityLink = () => {
    const intl = useIntl();

    return (
        <BlogLink
            imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/g5h75c61iryjfgxc47b76c644gk323uy.webp"
            imageAlt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.communityAltText",
                defaultMessage: "Community illustration"
            })}
            href="https://community.atlassian.com/"
            linkText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.communityLinkText",
                defaultMessage: "Jira community"
            })}
        />
    );
};

export const DevOpsLink = () => {
    const intl = useIntl();

    return (
        <BlogLink
            imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/n2a75c42wi3oj344bot150u635h5gns3.webp"
            imageAlt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.devOpsAltText",
                defaultMessage: "DevOps loop illustration"
            })}
            href={useLocalizedHref("/devops")}
            linkText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.blogsSection.devOpsLinkText",
                defaultMessage: "Learn DevOps best practices"
            })}
        />
    );
};

const BlogLink = (props: {
    imageSrc: string;
    imageAlt: string;
    href: string;
    linkText: string;
}) => {
    return (
        <div css={blogLinkStyles}>
            <a
                href={props.href}
                data-event-component="linkButton"
                data-event-container="blogsSection"
                data-event="clicked"
                data-label={props.linkText}
                data-product-key="jiraSoftware"
            >
                <Image
                    src={props.imageSrc}
                    alt={props.imageAlt}
                    aspectRatio="16/9"
                />
            </a>
            {blogLinkText(props.href, props.linkText)}
        </div>
    );
};

const blogLinkText = (href: string, linkText: string) => {
    return (
        <div>
            <a
                href={href}
                css={blogLinkTextStyles}
                data-event-component="linkButton"
                data-event-container="blogsSection"
                data-event="clicked"
                data-label={linkText}
                data-product-key="jiraSoftware"
            >
                <div
                    className="link-text"
                    css={css({
                        display: "inline-flex",
                        fontSize: token("space.200")
                    })}
                >
                    {linkText}
                    <div className="link-arrow">{linkArrow()}</div>
                </div>
            </a>
        </div>
    );
};

const linkArrow = () => {
    return (
        <svg
            css={css({
                marginInlineStart: token("space.075")
            })}
            fill="currentColor"
            width="11"
            height="8"
            viewBox="0 0 11 8"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.40796 0L6.32831 1.2021L8.07854 3.1497H0.763182C0.341906 3.1497 0 3.53038 0 3.99943C0 4.46849 0.341906 4.84917 0.763182 4.84917H8.07854L6.32831 6.7979L7.40796 8L11 3.99943L7.40796 0Z"
            />
        </svg>
    );
};

BlogsSection.displayName = "BlogsSection";

export default BlogsSection;
